/**
 * Table view — content area to the right of the sidebar.
 *
 * Visual style is from the Claude-Designer prototype: monospace data,
 * compact rows, sticky header, clean toolbar. The grid itself is Mantine
 * React Table v2 with our `kse-mrt-wrap` CSS layer overriding the chrome
 * to match the prototype.
 *
 * Edit / insert is handled via a Mantine modal (kept from the previous
 * implementation). A drawer-style edit panel is on the roadmap.
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router";
import {
  useQuery,
  useQueryClient,
  useMutation,
  keepPreviousData,
} from "@tanstack/react-query";
import {
  MantineReactTable,
  useMantineReactTable,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
} from "mantine-react-table";
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Text,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconArrowsSort,
  IconDownload,
  IconEdit,
  IconFileImport,
  IconPlus,
  IconReload,
  IconTrash,
} from "@tabler/icons-react";

import { ApiClient } from "../api/client";
import { qk, encodePk } from "../api/keys";
import type { ColumnMeta, RowsPage, TableDetail } from "../api/types";
import { DynamicField } from "../components/DynamicField";
import { TableSkeleton } from "../components/TableSkeleton";
import { ExportModal } from "../components/ExportModal";
import { ImportModal } from "../components/ImportModal";
import { InlineEditingSwitch } from "../components/InlineEditingSwitch";
import { useSetHeaderActions } from "../lib/header-actions";
import { useInlineEditing } from "../lib/inline-editing";
import { withPendingOp } from "../lib/pending-ops";
import { useActiveDb, withDb } from "../lib/active-db";

const STATUS_TONES: Record<string, "ok" | "warn" | "bad" | "off"> = {
  active: "ok", paid: "ok", delivered: "ok", completed: "ok", success: "ok",
  pending: "warn", processing: "warn", in_transit: "warn", review: "warn",
  cancelled: "bad", failed: "bad", refunded: "bad", inactive: "bad", error: "bad",
  draft: "off", archived: "off",
};

const NUMERIC_TYPES = new Set([
  "int", "bigint", "smallint", "tinyint",
  "decimal", "numeric", "money", "smallmoney",
  "float", "real",
]);

/**
 * Picks a default pixel width for a column.
 *
 * Computed from character counts:
 *   - data width  = (max char count of rendered values) × monospace
 *                   character width + cell padding
 *   - header width = (column-name char count) × UI-font character
 *                    width + header chrome (sort + kebab + PK/FK
 *                    badges)
 * The column's size is `max(headerWidth, dataWidth)` so neither the
 * header label nor the data value wraps or truncates at default
 * width. The user can still drag column edges to override.
 *
 * Reference values (max chars):
 *   datetime2(7)        2026-01-15T13:24:56.7890123     27
 *   datetimeoffset(7)   2026-01-15 13:24:56.7890123 +10:00  33
 *   uniqueidentifier    12345678-1234-1234-1234-1234567890ab  36
 *   bigint              -9223372036854775808            20
 */
// Calibrated against actual rendering (datetime2 27 chars in 271 px
// column showed only 17 chars → real per-char × char-count + chrome
// was ~271, working out to ~11–12 px/char and ~60 px chrome). Numbers
// are intentionally generous so columns never start truncated.
const DATA_CHAR_W = 12;    // monospace cell font (JetBrains Mono ~13px)
const HEADER_CHAR_W = 8;   // sans-serif UI font (Inter ~13px)
const DATA_CHROME  = 60;   // td padding + resize handle + sort/kebab share
const HEADER_CHROME = 70;  // sort arrow + kebab dots + base padding
const BADGE_CHROME = 28;   // each PK / FK badge in the header

function dataCharCount(dataType: string, maxLength: number | null): number {
  const dt = dataType.toLowerCase();
  if (dt === "bit") return 5;                    // "false"
  if (dt === "tinyint") return 3;                // 0..255
  if (dt === "smallint") return 6;               // -32768
  if (dt === "int") return 11;                   // -2147483648
  if (dt === "bigint") return 20;                // -9223372036854775808
  if (["decimal", "numeric", "money", "smallmoney"].includes(dt)) return 22;
  if (["float", "real"].includes(dt)) return 18;

  if (dt === "date") return 10;                  // 2026-01-15
  if (dt === "time") return 16;                  // 13:24:56.7890123
  if (dt === "smalldatetime") return 19;
  if (dt === "datetime") return 23;              // 2026-01-15 13:24:56.000
  if (dt === "datetime2") return 27;             // 2026-01-15T13:24:56.7890123
  if (dt === "datetimeoffset") return 33;        // ... +10:00

  if (dt === "uniqueidentifier") return 36;

  if (
    dt.startsWith("nvarchar") ||
    dt.startsWith("varchar") ||
    dt.startsWith("char") ||
    dt.startsWith("nchar")
  ) {
    if (maxLength == null || maxLength === 0) return 30;
    if (maxLength < 0) return 50;                // (MAX) — capped
    return Math.min(maxLength, 50);              // cap so varchar(4000) doesn't dominate
  }
  if (["text", "ntext", "xml"].includes(dt)) return 50;
  return 25;
}

function defaultColumnSize(
  dataType: string,
  maxLength: number | null,
  columnName: string,
  isPrimaryKey: boolean,
  hasFk: boolean,
): number {
  const dataChars = dataCharCount(dataType, maxLength);
  const headerChars = columnName.length;

  const headerChrome =
    HEADER_CHROME + (isPrimaryKey ? BADGE_CHROME : 0) + (hasFk ? BADGE_CHROME : 0);

  const dataWidth   = Math.ceil(dataChars   * DATA_CHAR_W   + DATA_CHROME);
  const headerWidth = Math.ceil(headerChars * HEADER_CHAR_W + headerChrome);

  return Math.max(dataWidth, headerWidth);
}

/**
 * Stringify a cell's raw value for use as the tooltip label. Mirrors
 * what `renderCellValue` shows visually for each type, so the tooltip
 * always matches the rendered text.
 */
function cellValueToString(value: unknown): string {
  if (value == null) return "NULL";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

/**
 * Wraps a cell's rendered content. Visually neutral — no ellipsis, no
 * forced sizing. Cells sit naturally in the column the same as before;
 * if the column happens to be narrower than the value, the browser
 * just clips at the cell boundary like it always did.
 *
 * The wrapper exists for the tooltip behaviour: on mouse-enter we
 * compare scrollWidth/clientWidth, and if the content actually doesn't
 * fit, a Mantine Tooltip with a 2-second open delay surfaces the full
 * value. Cells that fit don't show a tooltip at all.
 */
function TruncatableCell({
  tooltip,
  children,
}: {
  tooltip: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // +1 for sub-pixel rounding noise.
    setOverflowing(el.scrollWidth > el.clientWidth + 1);
  }, []);

  return (
    <Tooltip
      label={tooltip}
      openDelay={2000}
      disabled={!overflowing}
      withinPortal
      multiline
      maw={520}
      events={{ hover: true, focus: false, touch: false }}
    >
      <div
        ref={ref}
        onMouseEnter={checkOverflow}
        style={{
          overflow: "hidden",
          whiteSpace: "nowrap",
          // No textOverflow: ellipsis and no forced width — content
          // sits at its natural size; the surrounding td handles the
          // visual boundary as it did pre-wrapper.
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
}

function renderCellValue(value: unknown, col: ColumnMeta) {
  if (value == null) return <span className="kse-cell-null">NULL</span>;

  const dt = col.dataType.toLowerCase();
  const str = String(value);

  // Status pill — derived from value, not column type. Falls through to
  // generic rendering if the value isn't a known status keyword.
  const tone = STATUS_TONES[str.toLowerCase()];
  if (tone && col.name.toLowerCase().includes("status")) {
    return <span className="kse-status" data-tone={tone}>{str}</span>;
  }

  if (dt === "bit") {
    return <span className="kse-cell-bool" data-val={Boolean(value)}>{value ? "true" : "false"}</span>;
  }

  if (col.isPrimaryKey) {
    return <span className="kse-cell-pk">{str}</span>;
  }
  if (col.fk) {
    return <span className="kse-cell-fk">{str}</span>;
  }
  if (NUMERIC_TYPES.has(dt)) {
    return <span className="kse-cell-num">{str}</span>;
  }
  return <span>{str}</span>;
}

export function TableViewRoute({ api }: { api: ApiClient }) {
  const { schema = "", name = "" } = useParams();
  const qc = useQueryClient();
  const { enabled: inlineEditingEnabled } = useInlineEditing();
  const { activeId } = useActiveDb();

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const tableMetaQ = useQuery({
    enabled: !!activeId,
    queryKey: [...qk.table(schema, name), activeId],
    queryFn: () =>
      withPendingOp(`Loading schema for ${schema}.${name}…`, (signal) =>
        api.get<TableDetail>(
          withDb(`/api/tables/${schema}/${name}/columns`, activeId),
          { signal },
        ),
      ),
  });

  const queryParams = useMemo(() => {
    const sort = sorting.map((s) => `${s.id}:${s.desc ? "desc" : "asc"}`).join(",");
    const filter: Record<string, { op: string; value: unknown }> = {};
    for (const f of columnFilters) {
      if (f.value === "" || f.value == null) continue;
      filter[f.id] = { op: "contains", value: f.value };
    }
    return {
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sort: sort || undefined,
      filter: Object.keys(filter).length ? JSON.stringify(filter) : undefined,
    };
  }, [pagination, sorting, columnFilters]);

  const rowsQ = useQuery({
    enabled: !!tableMetaQ.data && !!activeId,
    queryKey: [...qk.rows(schema, name, queryParams), activeId],
    queryFn: () => {
      const search = new URLSearchParams();
      search.set("page", String(queryParams.page));
      search.set("pageSize", String(queryParams.pageSize));
      if (queryParams.sort) search.set("sort", queryParams.sort);
      if (queryParams.filter) search.set("filter", queryParams.filter);
      const url = withDb(
        `/api/tables/${schema}/${name}/rows?${search.toString()}`,
        activeId,
      );
      return withPendingOp(`Loading ${schema}.${name}…`, (signal) =>
        api.get<RowsPage>(url, { signal }),
      );
    },
    placeholderData: keepPreviousData,
  });

  const meta = tableMetaQ.data;

  // Refs that always point at the latest values, so callbacks captured
  // by the header-actions slot read fresh data when invoked instead of
  // closing over the (possibly empty) values from the initial render.
  const rowsDataRef = useRef<RowsPage | undefined>(undefined);
  rowsDataRef.current = rowsQ.data;
  const queryParamsRef = useRef(queryParams);
  queryParamsRef.current = queryParams;

  const columns = useMemo<MRT_ColumnDef<Record<string, unknown>>[]>(() => {
    if (!meta) return [];
    const canUpdate = meta.permissions.update;
    return meta.columns.map<MRT_ColumnDef<Record<string, unknown>>>((c) => {
      const isNumeric = NUMERIC_TYPES.has(c.dataType.toLowerCase());
      const isEditable =
        canUpdate && !c.isPrimaryKey && !c.isIdentity && !c.isComputed;
      return {
        accessorKey: c.name,
        header: c.name,
        size: defaultColumnSize(
          c.dataType,
          c.maxLength,
          c.name,
          c.isPrimaryKey,
          !!c.fk,
        ),
        minSize: 60,
        enableSorting: true,
        enableColumnFilter: true,
        enableEditing: isEditable,
        // Numeric columns get right-aligned cells (and headers) with
        // tabular figures, so digit columns visually line up. Per MRT
        // docs the `align` prop is the supported pattern for both head
        // and body cells. The custom className lets our CSS keep the
        // sort + kebab on the right side (MRT's default for align=right
        // is to row-reverse them onto the left).
        mantineTableHeadCellProps: isNumeric
          ? { align: "right", className: "kse-num-head" }
          : undefined,
        mantineTableBodyCellProps: isNumeric
          ? { align: "right", style: { fontVariantNumeric: "tabular-nums" } }
          : undefined,
        Header: () => (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              justifyContent: isNumeric ? "flex-end" : "flex-start",
              width: "100%",
            }}
          >
            {c.isPrimaryKey && <span className="kse-mark kse-mark--pk">PK</span>}
            {c.fk && <span className="kse-mark kse-mark--fk">FK</span>}
            <span>{c.name}</span>
          </span>
        ),
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return (
            <TruncatableCell tooltip={cellValueToString(v)}>
              {renderCellValue(v, c)}
            </TruncatableCell>
          );
        },
      };
    });
  }, [meta]);

  const insertMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      api.post<Record<string, unknown>>(
        withDb(`/api/tables/${schema}/${name}/rows`, activeId),
        values,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rows", schema, name] }),
  });

  const updateMut = useMutation({
    mutationFn: ({
      pk,
      values,
    }: {
      pk: Record<string, unknown>;
      values: Record<string, unknown>;
    }) =>
      api.patch<Record<string, unknown>>(
        withDb(`/api/tables/${schema}/${name}/rows/${encodePk(pk)}`, activeId),
        values,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rows", schema, name] }),
  });

  const deleteMut = useMutation({
    mutationFn: (pk: Record<string, unknown>) =>
      api.del(withDb(`/api/tables/${schema}/${name}/rows/${encodePk(pk)}`, activeId)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rows", schema, name] }),
  });

  const table = useMantineReactTable({
    columns,
    data: rowsQ.data?.rows ?? [],
    rowCount: rowsQ.data?.total ?? 0,
    state: {
      pagination,
      sorting,
      columnFilters,
      globalFilter,
      // We hide MRT's built-in loading UI (progress bar / overlay) and
      // render a Mantine `dots` loader ourselves so the loader style is
      // consistent across the app.
      isLoading: false,
      showProgressBars: false,
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    enableRowActions: meta
      ? meta.permissions.update || meta.permissions.delete
      : false,
    // Full MRT toolbar — global search, filter toggle, hide columns,
    // density toggle, fullscreen. Title + meta + refresh are injected
    // via `renderTopToolbarCustomActions` so they sit on the same row as
    // the toolbar icons.
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enableGlobalFilter: true,
    enableDensityToggle: true,
    enableFullScreenToggle: true,
    enableHiding: true,
    enableColumnFilters: true,
    // Subheader filter row — toggled by the toolbar's filter button.
    // The row is rendered as a `<tr>` inside `<thead>` so it pushes the
    // data tbody down naturally; no overlay.
    columnFilterDisplayMode: "subheader",
    // Per-column three-dots menu — sort asc/desc, hide column, pin
    // left/right, etc. Re-enabled per user request.
    enableColumnActions: true,
    // Column pinning — users can pin a column to the left or right
    // edge via the column-actions menu. Pinned columns stay visible
    // when horizontally scrolling the grid.
    enableColumnPinning: true,
    // Strip Mantine's default border/underline from per-column filter
    // inputs by using the unstyled variant. Our CSS handles typography
    // and placeholder colour.
    mantineFilterTextInputProps: { variant: "unstyled" },
    // Column resizing: drag column borders to adjust width. Combined with
    // per-column `size` defaults derived from data type, the user gets
    // smart initial widths plus full manual control.
    //
    // We deliberately leave `layoutMode` at its default ("semantic"). In
    // grid layout, MRT renders the filter row outside the <thead> flow,
    // which prevents it from pushing <tbody> down — the user-visible
    // bug where the filter inputs overlay the data rows.
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      // Headroom for long header labels and big string columns.
      // Per-column size from defaultColumnSize() already factors in
      // the data + header widths; this is just the upper bound on
      // user-resizing or when a column type isn't recognised.
      maxSize: 800,
      minSize: 80,
      size: 180,
    },
    // Server-side pagination already lazy-loads at the page level (50
    // rows per request). No row virtualization — it changes MRT's row
    // sizing to its own estimate and breaks the prototype's compact look.
    enableEditing: inlineEditingEnabled,
    editDisplayMode: "cell",
    // Multi-column sort: clicking a column header adds to the sort
    // stack instead of replacing it. Capped at 3 active sorts; users
    // see "1", "2", "3" badges on the column headers.
    enableMultiSort: true,
    isMultiSortEvent: () => true,
    maxMultiSortColCount: 3,
    mantineEditTextInputProps: ({ cell, row }) => {
      // Track Escape so the onBlur handler that follows doesn't try to
      // save the in-progress (cancelled) edit. We attach it to the input
      // element via dataset so the handler can read it without reaching
      // for a parent-scope ref.
      return {
        type: "text",
        variant: "unstyled",
        onKeyDown: (e) => {
          if (e.key === "Escape") {
            // Restore the original value in the DOM, mark it as cancelled,
            // then blur — this drops the user out of edit mode without
            // triggering a save.
            const original = row.original[cell.column.id];
            (e.currentTarget as HTMLInputElement).dataset.kseCancel = "1";
            (e.currentTarget as HTMLInputElement).value =
              original == null ? "" : String(original);
            e.currentTarget.blur();
          } else if (e.key === "Enter") {
            // Mantine TextInput doesn't blur on Enter by default — make
            // it commit on Enter for spreadsheet-like UX.
            e.currentTarget.blur();
          }
        },
        onBlur: (e) => {
          if (!meta) return;
          if ((e.currentTarget as HTMLInputElement).dataset.kseCancel === "1") {
            (e.currentTarget as HTMLInputElement).dataset.kseCancel = "";
            return;
          }
          const colId = cell.column.id;
          const newValueStr = e.currentTarget.value;
          const original = row.original;
          const oldValueStr =
            original[colId] == null ? "" : String(original[colId]);
          if (newValueStr === oldValueStr) return;
          const pk = pickPk(original, meta.primaryKey);
          if (!pk) {
            notifications.show({
              message: "Row has no primary key — cannot edit inline",
              color: "red",
            });
            return;
          }
          updateMut.mutate(
            {
              pk,
              values: { [colId]: newValueStr === "" ? null : newValueStr },
            },
            {
              onSuccess: () =>
                notifications.show({ message: "Saved", color: "teal" }),
              onError: (err) =>
                notifications.show({
                  message: `Save failed: ${(err as Error).message}`,
                  color: "red",
                }),
            },
          );
        },
      };
    },
    initialState: { density: "xs", showColumnFilters: false },
    positionActionsColumn: "last",
    mantinePaperProps: { withBorder: false, shadow: "none" },
    renderTopToolbarCustomActions: () => {
      if (!meta) return null;
      return (
        <Group gap="md" wrap="nowrap" className="kse-mrt-titleslot">
          <div className="kse-content__title">
            <span className="kse-content__title-schema">{meta.schema}.</span>
            <span className="kse-content__title-name">{meta.name}</span>
          </div>
          <div className="kse-content__meta">
            <div className="kse-content__meta-item">
              <strong>{(rowsQ.data?.total ?? 0).toLocaleString()}</strong>
              <span>rows</span>
            </div>
            <div className="kse-content__meta-item">
              <span>·</span>
              <strong>{meta.columns.length}</strong>
              <span>columns</span>
            </div>
          </div>
          <Tooltip label="Refresh">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => {
                // Force a refetch even if React Query thinks the data
                // is fresh (no staleTime set means invalidate alone
                // wouldn't necessarily trigger a network round-trip if
                // the cache hasn't been GC'd).
                qc.refetchQueries({ queryKey: qk.table(schema, name) });
                qc.refetchQueries({ queryKey: ["rows", schema, name] });
              }}
            >
              <IconReload size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      );
    },
    // Right-side toolbar group — replicate MRT's default internal
    // buttons and slot in our Clear Sort icon between density and
    // fullscreen so it lives with the other table-level controls.
    renderToolbarInternalActions: ({ table }) => {
      const sortCount = table.getState().sorting.length;
      // Two-level structure so we can give the inline-editing toggle
      // a visibly larger gap from the icon group (~2× the gap between
      // adjacent icons) without affecting the tight 4-px spacing
      // among the icons themselves.
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <InlineEditingSwitch />
          <Group gap={4} wrap="nowrap">
            <MRT_ToggleFiltersButton table={table} />
            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleDensePaddingButton table={table} />
            <Tooltip
              label={
                sortCount > 0
                  ? `Clear sort (${sortCount} active)`
                  : "Clear sort"
              }
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                disabled={sortCount === 0}
                onClick={() => table.resetSorting(true)}
                aria-label="Clear sort"
              >
                <IconArrowsSort size={18} />
              </ActionIcon>
            </Tooltip>
            <MRT_ToggleFullScreenButton table={table} />
          </Group>
        </div>
      );
    },
    renderRowActions: ({ row }) => {
      if (!meta) return null;
      const pk = pickPk(row.original, meta.primaryKey);
      return (
        <Group gap={2} wrap="nowrap">
          {meta.permissions.update && (
            <Tooltip label="Edit">
              <ActionIcon variant="subtle" size="sm" onClick={() => openEditModal(row.original)}>
                <IconEdit size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {meta.permissions.delete && pk && (
            <Tooltip label="Delete">
              <ActionIcon variant="subtle" size="sm" color="red" onClick={() => confirmDelete(pk)}>
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      );
    },
  });

  function openEditModal(initial: Record<string, unknown> | null) {
    if (!meta) return;
    modals.open({
      title: (
        <Text fw={700} fz="md">
          {initial
            ? `Edit row · ${meta.schema}.${meta.name}`
            : `Add row · ${meta.schema}.${meta.name}`}
        </Text>
      ),
      size: "lg",
      children: (
        <EditForm
          columns={meta.columns}
          initial={initial}
          onSubmit={async (values) => {
            try {
              if (initial) {
                const pk = pickPk(initial, meta.primaryKey);
                if (!pk) throw new Error("row has no PK; cannot update");
                await updateMut.mutateAsync({
                  pk,
                  values: stripPk(values, meta.primaryKey),
                });
                notifications.show({ message: "Row updated", color: "teal" });
              } else {
                await insertMut.mutateAsync(values);
                notifications.show({ message: "Row added", color: "teal" });
              }
              modals.closeAll();
            } catch (err) {
              notifications.show({
                message: `Save failed: ${(err as Error).message}`,
                color: "red",
              });
            }
          }}
          onCancel={() => modals.closeAll()}
        />
      ),
    });
  }

  function confirmDelete(pk: Record<string, unknown>) {
    modals.openConfirmModal({
      title: <Text fw={700} fz="md">Delete row?</Text>,
      children: <Text size="sm">This cannot be undone.</Text>,
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deleteMut.mutateAsync(pk);
          notifications.show({ message: "Row deleted", color: "teal" });
        } catch (err) {
          notifications.show({
            message: `Delete failed: ${(err as Error).message}`,
            color: "red",
          });
        }
      },
    });
  }

  // Inject Export + "+ New row" actions into the top bar's action slot.
  // Deps include the table identity (and current filter/sort signals) so
  // navigating between tables or changing filters refreshes the captured
  // closures — otherwise they'd point at the previous table's `meta` /
  // queryParams.
  useSetHeaderActions(
    meta ? (
      <Group gap="xs" wrap="nowrap">
        <Button
          size="xs"
          variant="default"
          leftSection={<IconDownload size={13} />}
          onClick={openExportModal}
        >
          Export
        </Button>
        {meta.permissions.insert && (
          <>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconFileImport size={13} />}
              onClick={() => setImportOpen(true)}
            >
              Import rows
            </Button>
            <Button
              size="xs"
              leftSection={<IconPlus size={13} />}
              onClick={() => openEditModal(null)}
            >
              New row
            </Button>
          </>
        )}
      </Group>
    ) : null,
    [
      meta?.schema,
      meta?.name,
      meta?.permissions.insert,
      queryParams.filter,
      queryParams.sort,
    ],
  );

  function openExportModal() {
    if (!meta) return;
    // Read the LATEST query state at click time via refs — closing over
    // `rowsQ.data` / `queryParams` directly would freeze them at the
    // render where the action button was last (re)created.
    const data = rowsDataRef.current;
    const qp = queryParamsRef.current;
    modals.open({
      title: <Text fw={700} fz="md">Export {meta.schema}.{meta.name}</Text>,
      size: "md",
      children: (
        <ExportModal
          schema={meta.schema}
          name={meta.name}
          columns={meta.columns}
          pageRows={data?.rows ?? []}
          totalAfterFilter={data?.total ?? 0}
          currentFilter={qp.filter}
          currentSort={qp.sort}
          fetchPage={async (page, pageSize, filter, sort, signal) => {
            const search = new URLSearchParams();
            search.set("page", String(page));
            search.set("pageSize", String(pageSize));
            if (sort) search.set("sort", sort);
            if (filter) search.set("filter", filter);
            return api.get(
              withDb(
                `/api/tables/${meta.schema}/${meta.name}/rows?${search.toString()}`,
                activeId,
              ),
              { signal },
            );
          }}
          onClose={() => modals.closeAll()}
        />
      ),
    });
  }

  if (!meta) {
    if (tableMetaQ.error) {
      return (
        <div className="kse-empty">
          <div>
            <div className="kse-empty__title">Could not load table</div>
            <div style={{ fontSize: "var(--t-md)", color: "var(--fg-muted)" }}>
              {(tableMetaQ.error as Error).message}
            </div>
          </div>
        </div>
      );
    }
    // Initial load (table list and/or this table's metadata not ready yet)
    // → show a table-shaped Mantine Skeleton so the layout doesn't pop in.
    return <TableSkeleton />;
  }


  return (
    <>
      {/* The title + row/column meta + refresh now live on the same row
          as MRT's toolbar (injected via renderTopToolbarCustomActions). */}
      <div className="kse-mrt-wrap" style={{ position: "relative" }}>
        {rowsQ.isLoading && !rowsQ.data ? (
          <TableSkeleton cols={meta.columns.length} />
        ) : (
          <MantineReactTable table={table} />
        )}
        {/* Dots overlay while a refetch is in flight (we already have data
            on screen). Initial loads use the skeleton above instead. */}
        {rowsQ.isFetching && rowsQ.data && (
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 16,
              zIndex: 5,
              pointerEvents: "none",
            }}
          >
            <Loader type="dots" size="sm" />
          </div>
        )}
      </div>

      <ImportModal
        api={api}
        schema={meta.schema}
        name={meta.name}
        columns={meta.columns}
        dbId={activeId}
        opened={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => {
          // Refresh the rows view so the imported rows show up.
          qc.refetchQueries({ queryKey: ["rows", schema, name] });
        }}
      />
    </>
  );
}

function pickPk(
  row: Record<string, unknown>,
  pk: string[],
): Record<string, unknown> | null {
  if (pk.length === 0) return null;
  const out: Record<string, unknown> = {};
  for (const c of pk) {
    if (row[c] === undefined || row[c] === null) return null;
    out[c] = row[c];
  }
  return out;
}

function stripPk(
  values: Record<string, unknown>,
  pk: string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...values };
  for (const c of pk) delete out[c];
  return out;
}

function EditForm({
  columns,
  initial,
  onSubmit,
  onCancel,
}: {
  columns: ColumnMeta[];
  initial: Record<string, unknown> | null;
  onSubmit: (values: Record<string, unknown>) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<Record<string, unknown>>(initial ?? {});
  const [submitting, setSubmitting] = useState(false);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try { await onSubmit(values); }
        finally { setSubmitting(false); }
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {columns
          .filter((c) => !c.isComputed)
          .map((c) => (
            <DynamicField
              key={c.name}
              column={c}
              value={values[c.name]}
              onChange={(v) => setValues((prev) => ({ ...prev, [c.name]: v }))}
              disabled={!!initial && c.isPrimaryKey}
            />
          ))}
        <Group justify="flex-end" gap="xs" mt="md">
          <Button variant="default" onClick={onCancel} disabled={submitting}>Cancel</Button>
          <Button type="submit" loading={submitting}>Save</Button>
        </Group>
      </div>
    </form>
  );
}
